// ═══════════════════════════════════════════════════════════════════════════════
// PracticeOS Lite — Shared UI Primitives
// Every view imports from here. Tokens come from lib/tokens.js
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useMemo, useRef, useEffect, useLayoutEffect, useId, forwardRef, Fragment, isValidElement, cloneElement } from "react";
import useIsMobile from "../hooks/useIsMobile";
import { createPortal } from "react-dom";
import { C } from "../lib/tokens";
import { adaptColor } from "../lib/adaptColor";
import { NC_PAYER_GROUPS, DEFAULT_APPT_TYPES } from "./constants";
import {
  IconCheck, IconUsers, IconClipboard, IconCalendar, IconEdit, IconChart, IconPill, IconFlask,
  IconMapPin, IconShield, IconSearch, IconBell, IconMessage, IconInbox, IconClock, IconReceipt,
  IconLogOut, IconLayers, IconFileText, IconCreditCard, IconSparkles, IconPhone,
} from "./icons";
import { emptyIconName } from "../lib/emptyIcons";

// Entrance-animation keyframes, injected once. Kept here (not in a CSS file) so
// the primitive library stays self-contained and inline-style-only. Guarded for
// SSR/test envs and against double-insertion. No hover-lift animations here -
// motion in the shared primitives is reserved for element entrances (modals,
// sheets), not pointer hover.
//
// One deliberate exception exists outside this file: the chart home section
// tiles in views/patient/ChartHome.jsx carry a press/lift treatment, because a
// grid of 21 flat tiles reads as static text rather than as navigation. It is
// scoped to that one component and ships with a prefers-reduced-motion opt-out.
// Adding hover motion to the primitives themselves is still off the table -
// it would apply to every button in the app at once.
if (typeof document !== "undefined" && !document.getElementById("pos-ui-anim")) {
  const posAnim = document.createElement("style");
  posAnim.id = "pos-ui-anim";
  posAnim.textContent =
    "@keyframes posFadeIn{from{opacity:0}to{opacity:1}}" +
    "@keyframes posModalIn{from{opacity:0;transform:translateY(8px) scale(0.98)}to{opacity:1;transform:none}}" +
    "@keyframes posSheetIn{from{transform:translateY(100%)}to{transform:translateY(0)}}" +
    "@keyframes posSkeleton{0%{background-position:200% 0}100%{background-position:-200% 0}}";
  document.head.appendChild(posAnim);
}

// ─── Badge ────────────────────────────────────────────────────────────────────
// Accepts label or children, and variant or color. Before Sep 2026 only
// label/variant were read, so <Badge color="red">Escalated - urgent</Badge>
// (the form used on the Outreach and Inbound SMS review screens) rendered an
// EMPTY pill in the default colour.
export const Badge = ({ label, children, variant, color, size = "sm", style }) => {
  const text = label != null ? label : children;
  const M = {
    teal:    { bg: "var(--brand-bg)",    color: "var(--brand-text)",           border: "var(--brand-border)" },
    amber:   { bg: C.amberBg,   color: C.amber,          border: C.amberBorder },
    red:     { bg: C.redBg,     color: C.red,            border: C.redBorder },
    blue:    { bg: C.blueBg,    color: C.blue,           border: C.blueBorder },
    purple:  { bg: C.purpleBg,  color: C.purple,         border: C.purpleBorder },
    green:   { bg: C.greenBg,   color: C.green,          border: C.greenBorder },
    neutral: { bg: C.bgSecondary, color: C.textSecondary, border: C.borderLight },
  };
  const s = M[variant || color] || M.teal;
  return (
    <span style={{
    // Long free-text labels (an AMH measure reference) must not push a row
    // grid out of alignment. Truncate rather than expand.
    maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-block", verticalAlign: "middle",
      fontSize: size === "xs" ? 9 : 10, fontWeight: 700,
      padding: size === "xs" ? "1px 6px" : "2px 9px",
      borderRadius: 20, background: s.bg, color: s.color,
      border: `0.5px solid ${s.border}`,
      textTransform: "uppercase", letterSpacing: "0.04em",
      ...(style || {}),
    }}>{text}</span>
  );
};

// ─── Button ───────────────────────────────────────────────────────────────────
// Flat color system with grounded affordance (June 2026). Commit variants are
// vibrant two-tone gradients (C) with a tight ground shadow + faint top-edge
// highlight so they read as real buttons on any palette, including the dark
// ones, with no hover lift and none of the old heavy gloss. Secondary variants
// are soft brand/amber tints with a colored 1.5px edge (D). Hover deepens the
// fill; mousedown scales the button inward (0.97). Pass `icon` (an SVG node) for
// a leading glyph - the flex layout centers and spaces it automatically.
export const Btn = ({ children, onClick, variant = "primary", size = "md", disabled = false, style = {}, type = "button", icon = null, title }) => {
  const V = {
    primary: {
      background: "var(--pos-btn-primary-bg)",
      color: "var(--brand-contrast, #fff)",
      border: "none",
      boxShadow: "0 1px 2px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.14)",
    },
    info: {
      background: "var(--pos-btn-info-bg)",
      color: C.onAccent, border: "none",
      boxShadow: "0 1px 2px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.14)",
    },
    danger: {
      background: "var(--pos-btn-danger-bg)",
      color: C.onAccent, border: "none",
      boxShadow: "0 1px 2px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.14)",
    },
    success: {
      background: "var(--pos-btn-success-bg)",
      color: C.onAccent, border: "none",
      boxShadow: "0 1px 2px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.14)",
    },
    ai: {
      background: "var(--pos-btn-ai-bg)",
      color: C.onAccent, border: "none",
      boxShadow: "0 1px 2px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.14)",
    },
    // Outline and ghost resolve through CSS variables so each theme can tune
    // them. Dark outline is a real outline (faint wash + lifted brand edge), not
    // a solid fill: a solid one made every secondary action look primary.
    // Fill/label contrast for every variant is gated by
    // scripts/check-button-contrast.mjs (4.5:1, both themes, rest and hover).
    outline: {
      background: "var(--pos-btn-outline-bg)",
      color: "var(--pos-btn-outline-fg)",
      border: "var(--pos-btn-outline-border)",
      boxShadow: "var(--pos-btn-outline-shadow)",
    },
    amber: {
      background: C.amberBg,
      color: C.amberText,
      border: `1.5px solid ${C.amber}`,
      boxShadow: "0 1px 2px " + C.hairline + "",
    },
    ghost: {
      background: "var(--pos-btn-ghost-bg)",
      color: "var(--pos-btn-ghost-fg)",
      border: "none",
      boxShadow: "var(--pos-btn-ghost-shadow)",
    },
  };
  const HOVER = {
    primary: { background: "var(--pos-btn-primary-hover)" },
    info:    { background: "var(--pos-btn-info-hover)" },
    danger:  { background: "var(--pos-btn-danger-hover)" },
    success: { background: "var(--pos-btn-success-hover)" },
    ai:      { background: "var(--pos-btn-ai-hover)" },
    outline: { boxShadow: "0 1px 2px " + C.hairline + ", inset 0 0 0 999px var(--pos-btn-wash)" },
    amber:   { boxShadow: "0 1px 2px " + C.hairline + ", inset 0 0 0 999px var(--pos-btn-wash)" },
    ghost:   { background: C.bgSecondary },
  };
  const S = {
    sm: { padding: "5px 12px", fontSize: 12 },
    md: { padding: "9px 18px", fontSize: 13 },
    lg: { padding: "12px 24px", fontSize: 14 },
  };
  // "secondary" is a legacy alias for the soft-tint outline treatment. Without
  // this it falls through to primary, so call sites passing variant="secondary"
  // were silently rendering as the brand primary button.
  const vkey = variant === "secondary" ? "outline" : variant;
  const base = V[vkey] || V.primary;
  const hov = HOVER[vkey] || {};
  // Disabled. Both themes set the --pos-btn-disabled-* tokens in styles.css: a
  // flat neutral surface with tertiary text. The old light look (the variant
  // at 40% opacity) put a white label on a pale tint at ~1.3:1, and dark had
  // the same problem at 2.25:1, so the button read as broken rather than
  // unavailable. The flat fill keeps the label near 4:1 for every practice
  // palette while the missing shadow and not-allowed cursor still say
  // "not yet". The variant values are only a fallback if the tokens vanish.
  const disabledLook = disabled ? {
    background: "var(--pos-btn-disabled-bg, " + base.background + ")",
    color: "var(--pos-btn-disabled-fg, " + base.color + ")",
    border: "var(--pos-btn-disabled-border, " + (base.border || "none") + ")",
    boxShadow: "var(--pos-btn-disabled-shadow, " + (base.boxShadow || "none") + ")",
  } : null;

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      onMouseEnter={(e) => {
        if (disabled) return;
        Object.assign(e.currentTarget.style, hov);
      }}
      onMouseLeave={(e) => {
        if (disabled) return;
        e.currentTarget.style.background = base.background;
        e.currentTarget.style.boxShadow = base.boxShadow || "none";
        e.currentTarget.style.transform = "scale(1)";
      }}
      onMouseDown={(e) => {
        if (disabled) return;
        e.currentTarget.style.transform = "scale(0.97)";
      }}
      onMouseUp={(e) => {
        if (disabled) return;
        e.currentTarget.style.transform = "scale(1)";
      }}
      style={{
        ...base, ...disabledLook, ...S[size], borderRadius: 8, fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? "var(--pos-btn-disabled-opacity, 0.4)" : 1,
        fontFamily: "inherit",
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
        transition: "background 0.15s ease, box-shadow 0.15s ease, transform 0.08s ease, opacity 0.15s",
        whiteSpace: "nowrap",
        ...style,
      }}
    >{icon ? <span style={{ display: "inline-flex", flexShrink: 0 }}>{icon}</span> : null}{children}</button>
  );
};

// ─── Filter Pill ──────────────────────────────────────────────────────────────
// Brand-tinted pill used for filter chips, date-range selectors, and
// segmented sub-tab navigation throughout the app. Active state reads from
// the brand palette CSS vars so switching the practice palette automatically
// retints every pill on screen.
//
// Distinct from TabBar (segmented bgSecondary control - "Pending / Ready /
// Held" style) and Btn (rectangular weighted buttons). FilterPill is the
// rounded, lightweight, low-stakes chip pattern.
//
// Usage:
//   <FilterPill active={status === "all"} onClick={() => setStatus("all")}>
//     All
//   </FilterPill>
//
// Sizes:
//   sm - dense rails (e.g. status filters with 6+ options)
//   md - default (e.g. date range, single-row filters)
export const FilterPill = ({ active, onClick, children, size = "md", style = {} }) => {
  const sizing = {
    sm: { padding: "3px 9px",  fontSize: 10, borderRadius: 12 },
    md: { padding: "4px 10px", fontSize: 11, borderRadius: 12 },
  };
  const s = sizing[size] || sizing.md;
  return (
    <button
      onClick={onClick}
      style={{
        padding: s.padding,
        border: `0.5px solid ${active ? "var(--brand-primary)" : C.borderMid}`,
        background: active ? "var(--brand-bg)" : C.bgPrimary,
        color: active ? C.brandText : C.textSecondary,
        borderRadius: s.borderRadius,
        fontSize: s.fontSize,
        fontWeight: active ? 600 : 500,
        cursor: "pointer",
        fontFamily: "inherit",
        transition: "background 0.12s, border-color 0.12s, color 0.12s",
        whiteSpace: "nowrap",
        ...style,
      }}
    >{children}</button>
  );
};

// ─── Card ─────────────────────────────────────────────────────────────────────
// className passthrough so the mobile helpers in styles.css (pos-rowgrid-scroll
// and friends) can be applied to a Card without wrapping it in a bare div.
// forwardRef so a moment (useMoment) or a scroll target can reach the card element.
//
// Keyboard access (round 4, Oct 2026; WCAG 2.1.1): a Card with onClick was a
// plain div that a keyboard or switch user could not reach, so prior auths,
// notes and consents opened only with a mouse. With onClick it is now a
// role="button" in the tab order that opens on Enter or Space. Keys pressed
// on a control INSIDE the card (a button, an input) are left to that control.
// The app-wide :focus-visible ring is a box-shadow, which the card's own
// inline shadow would hide, so the ring is drawn inline here while focus is
// keyboard focus. ariaLabel names the card when its text alone does not.
export const Card = forwardRef(function Card({ children, style = {}, onClick, elevation = "raised", className, ariaLabel }, ref) {
  // Elevation scale (UI Excellence item 2). Interactive cards lift one level on hover.
  const [hover, setHover] = useState(false);
  const [ring, setRing] = useState(false);
  const lvl = C.elevation || {};
  const base = onClick && hover ? (lvl.hover || C.shadowMd) : (lvl[elevation] !== undefined ? lvl[elevation] : C.shadowSm);
  const shadow = ring ? "0 0 0 2px " + C.bgPrimary + ", 0 0 0 4px " + C.teal + (base && base !== "none" ? ", " + base : "") : base;
  const keyProps = onClick ? {
    role: "button",
    tabIndex: 0,
    "aria-label": ariaLabel,
    onKeyDown: (e) => {
      if (e.target !== e.currentTarget) return;
      if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") { e.preventDefault(); onClick(e); }
    },
    onFocus: (e) => {
      if (e.target !== e.currentTarget) return;
      let kb = true;
      try { kb = e.currentTarget.matches(":focus-visible"); } catch (err) { kb = true; }
      setRing(kb);
    },
    onBlur: (e) => { if (e.target === e.currentTarget) setRing(false); },
  } : {};
  return (
    <div ref={ref} className={className} onClick={onClick} {...keyProps} onMouseEnter={onClick ? () => setHover(true) : undefined} onMouseLeave={onClick ? () => setHover(false) : undefined} style={{
      background: C.bgPrimary, border: `0.5px solid ${C.borderLight}`,
      borderRadius: 12, padding: "16px 18px", boxShadow: shadow,
      transform: onClick && hover ? "translateY(-1px)" : "none",
      transition: "box-shadow 220ms cubic-bezier(0.2,0.8,0.2,1), transform 220ms cubic-bezier(0.2,0.8,0.2,1)",
      cursor: onClick ? "pointer" : "default", outline: ring ? "none" : undefined, ...style,
    }}>{children}</div>
  );
});

// ─── Modal ────────────────────────────────────────────────────────────────────
// Renders a dialog with a teal hero header (title + optional subtitle/icon).
// Backward compatible: existing callers passing only { title, onClose, maxWidth }
// continue to work and inherit the new visual design automatically.
//
// Optional props:
//   subtitle - small descriptive line below the title in the hero strip
//   icon     - React node (typically an SVG) rendered in a circular badge
export const Modal = ({ children, title, subtitle = null, icon = null, onClose, maxWidth = 520, tone = "brand", onBack = null }) => {
  // Phones: full-width bottom sheet (rounded top, flush bottom) instead of a
  // floating centered card. Read once per render - no listener needed since a
  // mid-open rotation just re-renders on the next state change.
  const isNarrow = typeof window !== "undefined" && window.innerWidth <= 720;
  // Rendered into document.body. position:fixed resolves against the nearest
  // ancestor with a transform, filter, perspective, backdrop-filter or
  // contain - any of which turns a "full screen" overlay into a box clipped to
  // that ancestor. A portal makes the modal immune to whatever the page it was
  // opened from happens to be doing.
  const node = (
  <div
    role="dialog" aria-modal="true" aria-label={typeof title === "string" ? title : undefined}
    onClick={(e) => e.target === e.currentTarget && onClose && onClose()}
    style={{
      position: "fixed", inset: 0, background: C.scrim,
      backdropFilter: "blur(2px)", WebkitBackdropFilter: "blur(2px)",
      // Centering with alignItems clipped tall modals at BOTH edges, so the
      // header scrolled out of reach and the form could not be completed (seen
      // on Log touchpoint, which is 1120 wide with many fields). Scroll the
      // backdrop and centre with auto margins instead: short modals still sit
      // centred, tall ones start at the top and the whole dialog is reachable.
      display: "flex", alignItems: isNarrow ? "flex-end" : "flex-start", justifyContent: "center",
      overflowY: isNarrow ? "hidden" : "auto",
      zIndex: 300, padding: isNarrow ? 0 : 16,
      animation: "posFadeIn 0.15s ease",
    }}
  >
    <div style={{
      borderRadius: isNarrow ? "16px 16px 0 0" : 16, width: "100%",
      maxWidth: isNarrow ? "100%" : maxWidth, boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
      // Per-page text size (lib/textScale.js). zoom also scales vh, so the
      // height cap is divided back down to stay inside the viewport. Phones
      // keep the native sheet at 1x.
      zoom: isNarrow ? 1 : "var(--pos-text-scale, 1)",
      overflow: "hidden", maxHeight: isNarrow ? "94dvh" : "calc(92vh / var(--pos-text-scale, 1))", display: "flex", flexDirection: "column",
      margin: isNarrow ? 0 : "auto",   // vertical centring that yields when the modal is tall
      animation: isNarrow
        ? "posSheetIn 0.24s cubic-bezier(0.16,1,0.3,1)"
        : "posModalIn 0.2s cubic-bezier(0.16,1,0.3,1)",
    }}>
      {/* Brand glass hero header: translucent brand tint + backdrop blur frosts
          the dimmed app behind the modal. color-mix keeps it brand-themed with
          no extra CSS var. Modern browsers only; degrades to a translucent bar. */}
      <div style={{
        background: tone === "ai"
          ? "linear-gradient(135deg, color-mix(in srgb, #8b5cf6 72%, transparent) 0%, color-mix(in srgb, #6d28d9 85%, transparent) 100%)"
          : "linear-gradient(135deg, color-mix(in srgb, var(--brand-primary, #0F6E56) 72%, transparent) 0%, color-mix(in srgb, var(--brand-primary-dark, #085041) 85%, transparent) 100%)",
        backdropFilter: "blur(22px) saturate(170%)",
        WebkitBackdropFilter: "blur(22px) saturate(170%)",
        borderBottom: "1px solid rgba(255,255,255,0.16)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.22)",
        padding: "16px 20px",
        display: "flex", alignItems: "center", gap: 12,
        flexShrink: 0,
      }}>
        {onBack && (
          <button onClick={onBack} aria-label="Back" style={{
            background: "rgba(255,255,255,0.15)", border: "none",
            width: 28, height: 28, borderRadius: "50%",
            fontSize: 20, color: C.onAccent, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: "inherit", flexShrink: 0, padding: 0, lineHeight: 1,
          }}>{"\u2039"}</button>
        )}
        {icon && (
          <div style={{
            width: 36, height: 36, borderRadius: "50%",
            background: "rgba(255,255,255,0.18)",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>{icon}</div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: C.onAccent }}>{title}</div>
          {subtitle && (
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.85)", marginTop: 2, lineHeight: 1.4 }}>
              {subtitle}
            </div>
          )}
        </div>
        {onClose && (
          <button onClick={onClose} aria-label="Close" style={{
            background: "rgba(255,255,255,0.15)", border: "none",
            width: 28, height: 28, borderRadius: "50%",
            fontSize: 18, color: C.onAccent, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: "inherit", flexShrink: 0, padding: 0, lineHeight: 1,
          }}>×</button>
        )}
      </div>
       {/* pos-modal-body: styles.css keeps fields inside their grid cells and
           lets footer button rows wrap on phones (see "Modal bodies on phones"). */}
       <div className="pos-modal-body" style={{
         overflowY: "auto", padding: isNarrow ? 14 : 20, flex: 1, background: C.bgPrimary,
         // Bottom sheet: keep the last row of buttons above the iPhone home bar.
         paddingBottom: isNarrow ? "calc(14px + env(safe-area-inset-bottom, 0px))" : 20,
       }}>{children}</div>
    </div>
  </div>
  );
  return typeof document !== "undefined" ? createPortal(node, document.body) : node;
};

// ─── Modal Section ────────────────────────────────────────────────────────────
// Soft bgSecondary panel used inside Modal bodies to group related fields.
// Optional small uppercase label rendered above the panel.
//
// Usage:
//   <ModalSection label="Account info">
//     <Input label="Email" ... />
//     <Input label="Full name" ... />
//   </ModalSection>
export const ModalSection = ({ label, children, style = {} }) => (
  <div style={{ marginBottom: 14, ...style }}>
    {label && (
      <div style={{
        fontSize: 9, fontWeight: 700, color: C.textSecondary,
        textTransform: "uppercase", letterSpacing: 0.6,
        marginBottom: 8,
      }}>{label}</div>
    )}
    <div style={{
      background: C.bgSecondary,
      border: `0.5px solid ${C.borderLight}`,
      borderRadius: 8,
      padding: "12px 14px",
    }}>
      {children}
    </div>
  </div>
);

// ─── Info Box ─────────────────────────────────────────────────────────────────
// Tinted callout for "what happens next" / explanatory notes inside modals.
// Variants: teal (default), amber, red.
//
// Usage:
//   <InfoBox title="What happens next">A temp password and link will be...</InfoBox>
export const InfoBox = ({ children, variant = "teal", title }) => {
  const palettes = {
    // Text uses the brand TEXT colour (C.brandText): C.teal is the fill colour
    // and is too dark to read on the tinted background in dark mode.
    teal:  { bg: C.tealBg,  bd: C.tealBorder,  fg: C.brandText },
    amber: { bg: C.amberBg, bd: C.amberBorder, fg: C.amber },
    red:   { bg: C.redBg,   bd: C.redBorder,   fg: C.red },
  };
  const p = palettes[variant] || palettes.teal;
  return (
    <div style={{
      background: p.bg, border: `0.5px solid ${p.bd}`,
      borderRadius: 8, padding: "10px 12px",
      fontSize: 12, color: p.fg, lineHeight: 1.55,
      marginBottom: 14,
    }}>
      {title && (
        <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 4 }}>{title}</div>
      )}
      <div>{children}</div>
    </div>
  );
};
// ─── Field Label ──────────────────────────────────────────────────────────────
// Accepts style (Sep 2026): callers were already passing one, e.g. to drop the
// bottom margin when the label sits inline beside its input.
export const FL = ({ children, style }) => (
  <div style={{
    fontSize: 11, fontWeight: 600, color: C.textSecondary,
    textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 5,
    ...style,
  }}>{children}</div>
);

// ─── Input ────────────────────────────────────────────────────────────────────
export const Input = ({ label, value, onChange, type = "text", placeholder = "", style = {}, disabled = false, min, max }) => (
  <div style={{ marginBottom: 14, minWidth: 0, ...style }}>
    {label && <FL>{label}</FL>}
    <input
      type={type}
      value={value ?? ""}
      // Passed at a dozen call sites and dropped. A disabled input that still
      // accepts typing is a UI that lies about permissions; min and max were
      // being set on number fields that never enforced them.
      disabled={disabled}
      min={min}
      max={max}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        width: "100%", padding: "9px 12px",
        border: `1px solid ${C.borderMid}`, borderRadius: 8,
        fontSize: 13, fontFamily: "inherit", outline: "none",
        color: C.textPrimary, background: C.bgPrimary, boxSizing: "border-box",
        transition: "border-color 0.15s ease, box-shadow 0.15s ease",
      }}
      onFocus={(e) => {
        e.target.style.borderColor = "var(--brand-primary-mid)";
        e.target.style.boxShadow = "0 0 0 3px color-mix(in srgb, var(--brand-primary) 16%, transparent)";
      }}
      onBlur={(e) => {
        e.target.style.borderColor = C.borderMid;
        e.target.style.boxShadow = "none";
      }}
    />
  </div>
);

// ─── Textarea ─────────────────────────────────────────────────────────────────
// Auto-expanding textarea. Grows to fit content as the user types, with a
// minimum height derived from `rows` (default 3). Resize remains "vertical"
// so the user can manually pull the corner taller if they want a larger
// editing surface than the content demands - mostly a power-user
// affordance, the auto-grow does the right thing without it.
//
// Mechanics: on every value change, reset height to "auto" so scrollHeight
// reports the natural content height, then set height to that. The reset
// step matters because scrollHeight only grows; without it the textarea
// would shrink-resist when the user deletes content.
//
// minHeight derived from `rows` * line-height (~20px) + vertical padding
// to match the default-rendered initial height before any content lands.
export const Textarea = ({ label, value, onChange, rows = 3, placeholder = "" }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = el.scrollHeight + "px";
  }, [value]);
  return (
    <div style={{ marginBottom: 14 }}>
      {label && <FL>{label}</FL>}
      <textarea
        ref={ref}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        style={{
          width: "100%", padding: "9px 12px",
          border: `1px solid ${C.borderMid}`, borderRadius: 8,
          fontSize: 13, fontFamily: "inherit", outline: "none",
          color: C.textPrimary, resize: "vertical",
          background: C.bgPrimary, boxSizing: "border-box",
          minHeight: rows * 20 + 18,
          overflow: "hidden",
          transition: "border-color 0.15s ease, box-shadow 0.15s ease",
        }}
        onFocus={(e) => {
          e.target.style.borderColor = "var(--brand-primary-mid)";
          e.target.style.boxShadow = "0 0 0 3px color-mix(in srgb, var(--brand-primary) 16%, transparent)";
        }}
        onBlur={(e) => {
          e.target.style.borderColor = C.borderMid;
          e.target.style.boxShadow = "none";
        }}
      />
    </div>
  );
};

// ─── Select ───────────────────────────────────────────────────────────────────
// disabled and placeholder are honoured (Sep 2026). Before, Practice Settings
// passed disabled={!canEdit} for read-only users and it was dropped, so the
// Timezone field stayed editable while every field around it was locked.
export const Select = ({ label, value, onChange, options, style = {}, disabled = false, placeholder, ariaLabel }) => (
  <div style={{ marginBottom: 14, minWidth: 0, ...style }}>
    {label && <FL>{label}</FL>}
    <select
      aria-label={ariaLabel}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      style={{
        opacity: disabled ? 0.6 : 1, cursor: disabled ? "not-allowed" : "pointer",
        width: "100%", padding: "9px 12px",
        border: `1px solid ${C.borderMid}`, borderRadius: 8,
        fontSize: 13, fontFamily: "inherit", outline: "none",
        color: C.textPrimary, background: C.bgPrimary, boxSizing: "border-box",
        WebkitAppearance: "none",
        backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b6a63' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")",
        backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center",
        paddingRight: 32,
        transition: "border-color 0.15s ease, box-shadow 0.15s ease",
      }}
      onFocus={(e) => {
        e.target.style.borderColor = "var(--brand-primary-mid)";
        e.target.style.boxShadow = "0 0 0 3px color-mix(in srgb, var(--brand-primary) 16%, transparent)";
      }}
      onBlur={(e) => {
        e.target.style.borderColor = C.borderMid;
        e.target.style.boxShadow = "none";
      }}
    >
      {placeholder && <option value="" disabled>{placeholder}</option>}
      {options.map((o) =>
        typeof o === "string"
          ? <option key={o} value={o}>{o}</option>
          : <option key={o.value} value={o.value}>{o.label}</option>
      )}
    </select>
  </div>
);

// ─── Toggle ───────────────────────────────────────────────────────────────────
// A switch (round 7 #6). It used to be a bare <button>: a screen reader heard
// "button" with no name and no state (Settings > Hours read seven of them in a
// row). Now it is role="switch" with aria-checked, named by `label` (the same
// words as the visible text beside it, so voice control can say them) or by
// `aria-labelledby` pointing at that text. scripts/check-clickable-a11y.mjs
// fails a Toggle call site with neither. The pos-switch class adds a focus ring that
// follows the pill shape (styles.css). type="button" so a switch inside a
// <form> never submits it.
// disabled was being PASSED at several call sites and silently dropped, so a
// user without edit rights got a fully interactive control that lied about
// what they could do. Honouring it on the element is the actual fix; the
// visual state exists so the refusal is visible before the click.
export const Toggle = ({ value, onChange, disabled = false, label, "aria-labelledby": labelledBy, id }) => (
  <button
    type="button"
    role="switch"
    aria-checked={!!value}
    aria-label={labelledBy ? undefined : label}
    aria-labelledby={labelledBy}
    id={id}
    className="pos-switch"
    disabled={disabled}
    onClick={() => { if (!disabled) onChange(!value); }}
    style={{
      width: 44, height: 24, borderRadius: 12, border: "none",
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.5 : 1,
      background: value ? "var(--brand-primary-mid)" : C.borderMid,
      transition: "background 0.2s", position: "relative", flexShrink: 0,
    }}
  >
    <div style={{
      width: 18, height: 18, borderRadius: 8, background: C.bgPrimary,
      position: "absolute", top: 3, left: value ? 23 : 3,
      transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
    }} />
  </button>
);

// ─── Avatar ───────────────────────────────────────────────────────────────────
export const Avatar = ({ initials, size = 32, color }) => {
  const finalColor = color || "var(--brand-primary)";
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: finalColor, color: "var(--brand-contrast)", display: "flex",
      alignItems: "center", justifyContent: "center",
      fontSize: size * 0.35, fontWeight: 700, flexShrink: 0,
    }}>{initials}</div>
  );
};

// ─── Section Head ─────────────────────────────────────────────────────────────
export const SectionHead = ({ title, sub, action }) => (
  <div style={{
    display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 14,
  }}>
    <div>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.textPrimary }}>{title}</div>
      {sub && <div style={{ fontSize: 11, color: C.textTertiary, marginTop: 2 }}>{sub}</div>}
    </div>
    {action}
  </div>
);

// ─── Top Bar ──────────────────────────────────────────────────────────────────
// Page header strip used at the top of full-page views (Patients, Billing
// Queue, etc.). Renders a brand-tinted gradient that fades from --brand-bg
// at the top into --brand-canvas at the bottom, so the strip flows seamlessly
// into body content sitting on the canvas tint. Per-palette: navy practice
// shows navy wash, plum shows plum wash, etc. Mirrors the SELECTED MEASURE
// panel recipe in AmhQualityDashboardTab.jsx for a coherent app-wide feel.
//
// Phones (<= 720px, round 3 "content first"). The strip used to print a 22px
// title, every action and every filter above the content, which pushed the
// first Queue patient to y=822 of 844. On a phone it now:
//   - drops the big title when the phone top bar (Layout) already says the
//     same thing. The h1 stays for screen readers, visually hidden.
//   - keeps ONE primary action visible and puts the rest behind a "..." menu.
//   - moves filter controls into a bottom sheet behind a Filters button that
//     carries an active-count badge.
// Opt-in safe: the header only rearranges what it can classify.
//   * `phone={{ keep, primary, menu }}` says it explicitly. keep = nodes that
//     stay in view (a live-status pill, a view picker), primary = the one
//     button that stays, menu = [{ key, label, onSelect, icon, disabled,
//     testId, title }].
//   * Without `phone`, `actions` made ONLY of <Btn> elements (two or more) is
//     classified automatically: the first primary-variant Btn stays, the rest
//     go to the menu. Anything else in `actions` (a TabBar, a raw <button>, a
//     wrapper span) leaves today's behaviour untouched.
//   * `phoneFilters` (node) + `phoneFilterCount` + optional
//     `onClearPhoneFilters`: the view decides which controls are filters and
//     stops rendering them inline on a phone.
// Desktop ignores every phone-only prop, so it renders exactly as before.

// "Live Queue" and the bar's "Queue" are the same place: compare word sets,
// and only for short titles - "Compliance is for practice leads" (an access
// notice) must stay visible even though the bar says "Compliance".
const titleWords = (t) => String(t || "").toLowerCase().replace(/[^a-z0-9 ]+/g, " ").split(/\s+/).filter(Boolean);
export function sameTopBarTitle(barText, title) {
  const a = titleWords(barText), b = titleWords(title);
  if (!a.length || !b.length) return false;
  if (a.join(" ") === b.join(" ")) return true;
  if (a.length > 3 || b.length > 3) return false;
  const [small, big] = a.length <= b.length ? [a, b] : [b, a];
  return small.every((w) => big.includes(w));
}

// Flatten an `actions` node (fragments, arrays, conditionals) into elements.
function flattenActions(node) {
  const out = [];
  const walk = (n) => {
    if (n == null || n === false || n === true || n === "") return;
    if (Array.isArray(n)) { n.forEach(walk); return; }
    if (isValidElement(n) && n.type === Fragment) { walk(n.props.children); return; }
    out.push(n);
  };
  walk(node);
  return out;
}

// Returns { primary, menu } when every action is a labelled <Btn>, else null
// (unclassifiable: the caller keeps today's layout).
export function classifyTopBarActions(actions) {
  const items = flattenActions(actions);
  if (items.length < 2) return null;
  if (!items.every((el) => isValidElement(el) && el.type === Btn)) return null;
  // An icon-only Btn ("?") has no label to show in a menu: leave it alone.
  const hasText = (el) => {
    const c = el.props.children;
    if (typeof c === "string") return c.trim() !== "";
    return typeof c === "number" || (c != null && typeof c !== "boolean");
  };
  if (!items.every(hasText)) return null;
  const pi = items.findIndex((el) => (el.props.variant || "primary") === "primary");
  const primary = pi >= 0 ? items[pi] : null;
  const menu = items.filter((_, i) => i !== pi).map((el, i) => ({
    key: "a" + i, label: el.props.children, icon: el.props.icon || null,
    onSelect: el.props.onClick, disabled: !!el.props.disabled, title: el.props.title,
  }));
  return { primary, menu };
}

const SR_ONLY = {
  position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden",
  clip: "rect(0 0 0 0)", whiteSpace: "nowrap", border: 0,
};

// Phone actions overflow menu: aria-haspopup menu, arrow keys / Home / End,
// Escape returns focus to the button, Tab or an outside tap closes it. Items
// are 44px tall (touch target).
export function TopBarMenu({ items, label = "More actions" }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const wrapRef = useRef(null);
  const menuId = useId();
  const list = (items || []).filter(Boolean);
  const focusItem = (i) => {
    const els = menuRef.current ? Array.from(menuRef.current.querySelectorAll("[role=menuitem]")) : [];
    if (els.length) els[(i + els.length) % els.length].focus();
  };
  const close = (refocus) => { setOpen(false); if (refocus && btnRef.current) btnRef.current.focus(); };
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => focusItem(0), 0);
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    // Escape anywhere (not only from an item: focus may still be on the
    // button in the frame before the first item takes it).
    const onEsc = (e) => { if (e.key === "Escape") { e.preventDefault(); close(true); } };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    document.addEventListener("keydown", onEsc);
    return () => { clearTimeout(t); document.removeEventListener("mousedown", onDown); document.removeEventListener("touchstart", onDown); document.removeEventListener("keydown", onEsc); };
  }, [open]);
  if (!list.length) return null;
  const onMenuKey = (e) => {
    const els = Array.from(menuRef.current.querySelectorAll("[role=menuitem]"));
    const i = els.indexOf(document.activeElement);
    if (e.key === "ArrowDown") { e.preventDefault(); focusItem(i + 1); }
    else if (e.key === "ArrowUp") { e.preventDefault(); focusItem(i - 1); }
    else if (e.key === "Home") { e.preventDefault(); focusItem(0); }
    else if (e.key === "End") { e.preventDefault(); focusItem(els.length - 1); }
    else if (e.key === "Tab") { setOpen(false); }
  };
  return (
    <div ref={wrapRef} style={{ position: "relative", flexShrink: 0 }}>
      <button ref={btnRef} type="button" data-testid="topbar-more" aria-label={label} title={label}
        aria-haspopup="menu" aria-expanded={open} aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => { if (e.key === "ArrowDown" && !open) { e.preventDefault(); setOpen(true); } }}
        style={{
          width: 44, height: 44, borderRadius: 8, cursor: "pointer", fontFamily: "inherit",
          border: "0.5px solid " + C.borderMid, background: open ? C.bgSecondary : C.bgPrimary, color: C.textPrimary,
          display: "flex", alignItems: "center", justifyContent: "center", padding: 0,
        }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <circle cx="5" cy="12" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="19" cy="12" r="1.8" />
        </svg>
      </button>
      {open && (
        <div ref={menuRef} id={menuId} role="menu" aria-label={label} data-testid="topbar-menu" onKeyDown={onMenuKey}
          style={{
            position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 60, minWidth: 220, maxWidth: "calc(100vw - 32px)",
            background: C.bgPrimary, border: "0.5px solid " + C.borderLight, borderRadius: 12, boxShadow: C.shadowLg,
            padding: 4, animation: "posFadeIn 0.12s ease",
          }}>
          {list.map((it, i) => (
            <button key={it.key || i} type="button" role="menuitem" data-testid={it.testId} title={it.title}
              disabled={it.disabled} aria-disabled={it.disabled || undefined}
              onClick={() => { if (it.disabled) return; setOpen(false); if (it.onSelect) it.onSelect(); }}
              style={{
                display: "flex", alignItems: "center", gap: 10, width: "100%", minHeight: 44, padding: "0 12px",
                border: "none", background: "transparent", borderRadius: 8, textAlign: "left", fontFamily: "inherit",
                fontSize: 14, fontWeight: 500, color: it.disabled ? C.textTertiary : C.textPrimary,
                cursor: it.disabled ? "not-allowed" : "pointer", whiteSpace: "nowrap",
              }}
              onFocus={(e) => { e.currentTarget.style.background = C.bgSecondary; }}
              onBlur={(e) => { e.currentTarget.style.background = "transparent"; }}>
              {it.icon ? <span aria-hidden="true" style={{ display: "inline-flex", color: C.textSecondary, flexShrink: 0 }}>{it.icon}</span> : null}
              <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{it.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Filters button + bottom sheet. Filters apply live (the controls inside are
// the view's own controlled inputs), so the sheet only needs Done and, when
// the view can reset them, Clear.
export function TopBarFilters({ children, count = 0, onClear }) {
  const [open, setOpen] = useState(false);
  const n = Number(count) || 0;
  return (
    <>
      <button type="button" data-testid="topbar-filters" onClick={() => setOpen(true)}
        aria-haspopup="dialog" aria-label={n ? "Filters, " + n + " active" : "Filters"}
        style={{
          minHeight: 44, padding: "0 12px", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", flexShrink: 0,
          border: "0.5px solid " + (n ? "var(--brand-border)" : C.borderMid),
          background: n ? "var(--brand-bg)" : C.bgPrimary, color: n ? "var(--brand-text)" : C.textPrimary,
          fontSize: 13, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6,
        }}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <path d="M4 6h16M7 12h10M10 18h4" />
        </svg>
        Filters
        {n > 0 && (
          <span data-testid="topbar-filters-count" style={{
            minWidth: 18, height: 18, padding: "0 5px", borderRadius: 999, fontSize: 11, fontWeight: 700,
            background: "var(--brand-primary)", color: "var(--brand-contrast, #fff)",
            display: "inline-flex", alignItems: "center", justifyContent: "center", fontVariantNumeric: "tabular-nums",
          }}>{n}</span>
        )}
      </button>
      {open && (
        <Modal title="Filters" onClose={() => setOpen(false)}>
          <div data-testid="topbar-filters-sheet" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {children}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            {onClear && n > 0 && (
              <Btn variant="outline" onClick={onClear} style={{ flex: 1, minHeight: 44 }}>Clear</Btn>
            )}
            <span data-testid="topbar-filters-done" style={{ display: "contents" }}>
              <Btn variant="primary" onClick={() => setOpen(false)} style={{ flex: 1, minHeight: 44 }}>Done</Btn>
            </span>
          </div>
        </Modal>
      )}
    </>
  );
}

// The phone top bar (Layout) marks its title span with data-pos-phone-title.
// Watch it so a view that refines that title later (usePhoneTitle) brings the
// big title back if the two stop matching.
function useBarShowsTitle(onPhone, title) {
  const [shows, setShows] = useState(false);
  useLayoutEffect(() => {
    if (!onPhone || typeof title !== "string" || typeof document === "undefined") { setShows(false); return undefined; }
    const el = document.querySelector("[data-pos-phone-title]");
    if (!el) { setShows(false); return undefined; }
    const read = () => setShows(sameTopBarTitle(el.textContent, title));
    read();
    if (typeof MutationObserver === "undefined") return undefined;
    const mo = new MutationObserver(read);
    mo.observe(el, { childList: true, characterData: true, subtree: true });
    return () => mo.disconnect();
  }, [onPhone, title]);
  return shows;
}

// The one action that stays on a phone gets a 44px touch target.
const tallen = (el) => (isValidElement(el) ? cloneElement(el, { style: { ...(el.props.style || {}), minHeight: 44 } }) : el);

export const TopBar = ({ title, sub, actions, phone, phoneFilters, phoneFilterCount = 0, onClearPhoneFilters }) => {
  const isPhone = useIsMobile();
  const barShowsTitle = useBarShowsTitle(isPhone, title);
  const hideTitle = isPhone && barShowsTitle;
  const titleEl = (
    <h1 style={hideTitle ? SR_ONLY : { margin: 0, fontSize: 22, fontWeight: 700, color: C.textPrimary, letterSpacing: "-0.01em" }}>{title}</h1>
  );

  // Phone rearrangement: explicit `phone`, else auto-classified Btn actions.
  const plan = !isPhone ? null
    : phone ? { keep: phone.keep || null, primary: phone.primary || null, menu: (phone.menu || []).filter(Boolean), menuLabel: phone.menuLabel }
    : (() => { const c = classifyTopBarActions(actions); return c ? { keep: null, primary: c.primary, menu: c.menu } : null; })();
  const filtersBtn = isPhone && phoneFilters
    ? <TopBarFilters count={phoneFilterCount} onClear={onClearPhoneFilters}>{phoneFilters}</TopBarFilters>
    : null;

  if (isPhone && (plan || filtersBtn)) {
    const keep = plan ? plan.keep : null;
    // A short summary ("3 open") shares the controls row; a sentence gets its
    // own line rather than an ellipsis.
    const subInline = hideTitle && !keep && sub && typeof sub === "string" && sub.length <= 32;
    // Filters only (no plan): the view's own actions stay, at touch height.
    const ownActions = plan ? null : flattenActions(actions).map((el, i) => (isValidElement(el) && el.type === Btn
      ? cloneElement(tallen(el), { key: el.key || "act" + i })
      : (isValidElement(el) ? cloneElement(el, { key: el.key || "act" + i }) : el)));
    const controls = (
      <>
        {filtersBtn}
        {plan ? (plan.primary ? tallen(plan.primary) : null) : ownActions}
        {plan ? <TopBarMenu items={plan.menu} label={plan.menuLabel} /> : null}
      </>
    );
    return (
      <div data-testid="topbar" data-phone-compact="1" style={{
        background: "linear-gradient(180deg, var(--brand-bg, " + C.tealLight + ") 0%, var(--brand-canvas, " + C.bgTertiary + ") 100%)",
        borderBottom: `0.5px solid ${C.borderLight}`, position: "relative", zIndex: 5,
        padding: "8px 16px", display: "flex", flexDirection: "column", gap: 8, flexShrink: 0,
      }}>
        {hideTitle ? titleEl : <div>{titleEl}</div>}
        {sub && !subInline && (
          <div style={{ fontSize: 12, color: C.textSecondary, lineHeight: 1.4 }}>{sub}</div>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          {keep ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flex: "1 1 auto" }}>{keep}</div>
          ) : subInline ? (
            <div style={{ fontSize: 13, color: C.textSecondary, minWidth: 0, flex: "1 1 auto", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sub}</div>
          ) : <div style={{ flex: "1 1 auto" }} />}
          {controls}
        </div>
      </div>
    );
  }

  // Nothing left to show on a phone once the title is in the phone bar: keep
  // only the (hidden) heading, not an empty tinted strip.
  if (hideTitle && !sub && !actions) return titleEl;

  return (
    <div data-testid="topbar" style={{
      background: "linear-gradient(180deg, var(--brand-bg, " + C.tealLight + ") 0%, var(--brand-canvas, " + C.bgTertiary + ") 100%)",
      borderBottom: `0.5px solid ${C.borderLight}`,
      padding: isPhone ? "10px 16px" : "12px 24px", minHeight: hideTitle ? 0 : 72, display: "flex",
      alignItems: "center", justifyContent: "space-between", flexShrink: 0,
      flexWrap: "wrap", rowGap: 10, position: hideTitle ? "relative" : undefined,
    }}>
      <div>
        {titleEl}
        {sub && <div style={{ fontSize: hideTitle ? 12 : 13, color: C.textSecondary, marginTop: hideTitle ? 0 : 2 }}>{sub}</div>}
      </div>
      {/* maxWidth / minWidth: a wide TabBar here must scroll inside itself, not push the whole page sideways on a phone. */}
      {actions && <div className="pos-no-print" style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", maxWidth: "100%", minWidth: 0 }}>{actions}</div>}
    </div>
  );
};

// ─── Tab Bar ──────────────────────────────────────────────────────────────────
// Segmented sub-tab control. Sits inside the gradient-tinted TopBar so the
// wrapper is plain white with a thin hairline border to read as a defined
// control on the gradient. Active state uses var(--brand-bg) tint + brand
// primary text so the selected tab picks up the practice palette automatically.
// Phones show a native picker once a bar has this many tabs (Oct 2026: was
// above six; Eligibility's five tabs scrolled off the edge on a phone, while
// the Network console's picker showed every section in one tap).
export const TABBAR_PICKER_MIN = 4;
export const TabBar = ({ tabs, active, onChange, ariaLabel = "Section" }) => {
  const [isPhone, setIsPhone] = useState(() => (typeof window !== "undefined" ? window.innerWidth <= 720 : false));
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 720px)");
    const h = (e) => setIsPhone(e.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);
  // Sliding indicator (UI Excellence item 4): one absolutely positioned pill
  // moves between tabs instead of each tab restyling itself.
  const barRef = useRef(null);
  const [ind, setInd] = useState({ left: 0, width: 0, ready: false });
  useEffect(() => {
    const bar = barRef.current; if (!bar) return;
    const el = bar.querySelector('[data-tab="' + String(active) + '"]');
    if (!el) return;
    setInd({ left: el.offsetLeft, width: el.offsetWidth, ready: true });
    if (el.scrollIntoView) { try { el.scrollIntoView({ block: "nearest", inline: "nearest" }); } catch (e) { /* older browsers */ } }
    // Labels too: counts like "Changes (3)" load after the first paint and
    // shift every tab, which left the pill under the wrong one.
  }, [active, tabs.length, tabs.map((t) => t[1]).join("|")]);
  // Edge fades (Oct 2026): a strip that scrolls showed no sign of it - on a
  // phone the Inbox's "Escalations" was simply cut off. Fade whichever edge
  // has more tabs behind it; no fade when everything fits.
  const [edges, setEdges] = useState({ l: false, r: false });
  useEffect(() => {
    const bar = barRef.current; if (!bar) return undefined;
    const measure = () => {
      const max = bar.scrollWidth - bar.clientWidth;
      const l = bar.scrollLeft > 2, r = max - bar.scrollLeft > 2;
      setEdges((p) => (p.l === l && p.r === r ? p : { l, r }));
    };
    measure();
    bar.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);
    return () => { bar.removeEventListener("scroll", measure); window.removeEventListener("resize", measure); };
  }, [tabs.length, tabs.map((t) => t[1]).join("|"), isPhone]);
  const fadeMask = edges.l || edges.r
    ? "linear-gradient(to right, " + (edges.l ? "transparent 0, #000 28px" : "#000 0") + ", " + (edges.r ? "#000 calc(100% - 28px), transparent 100%" : "#000 100%") + ")"
    : undefined;
  return (
  // Scrolls rather than squeezes. Flex items shrink by default, so a six-option
  // bar on a phone compressed every label until the last one was clipped
  // instead of the bar becoming scrollable - which is how "Denied" ended up
  // hanging off the edge of Billing Readiness.
  //
  // Wrapping was the other option and is wrong here: a segmented control reads
  // as ONE control, and a second row of it reads as two.
  // Many tabs on a small screen: a scrolling strip hides most of them behind a
  // gesture, and a fade only hints that something is there. With four or more
  // tabs on a phone, render a native picker instead - every option visible in
  // one tap, and it uses the platform's own control (the Network console
  // pattern). Two or three tabs stay a segmented control.
  isPhone && tabs.length >= TABBAR_PICKER_MIN ? (
    <select
      className="pos-tabbar-picker"
      value={String(active)}
      onChange={(e) => onChange(tabs.find(([v]) => String(v) === e.target.value)?.[0] ?? e.target.value)}
      aria-label={ariaLabel}
      style={{
        width: "100%", minWidth: 0, padding: "9px 12px", fontSize: 13, fontWeight: 600,
        fontFamily: "inherit", color: C.textPrimary, background: C.bgPrimary,
        border: "0.5px solid " + C.borderMid, borderRadius: 8,
      }}
    >
      {tabs.map(([v, l]) => <option key={v} value={String(v)}>{l}</option>)}
    </select>
  ) : (
  <div ref={barRef} className="pos-tabbar-scroll" data-fade={fadeMask ? (edges.l ? "l" : "") + (edges.r ? "r" : "") : undefined} style={{
    position: "relative",
    display: "flex", background: C.bgPrimary, border: "0.5px solid " + C.borderLight,
    borderRadius: 8, padding: 3, gap: 2,
    overflowX: "auto", WebkitOverflowScrolling: "touch",
    scrollbarWidth: "none",
    ...(fadeMask ? { WebkitMaskImage: fadeMask, maskImage: fadeMask } : {}),
  }}>
    {ind.ready && (
      <div aria-hidden="true" style={{
        position: "absolute", top: 3, bottom: 3, left: ind.left, width: ind.width,
        background: "var(--brand-bg)", borderRadius: 6, boxShadow: C.shadowXs,
        transition: "left 220ms cubic-bezier(0.2,0.8,0.2,1), width 220ms cubic-bezier(0.2,0.8,0.2,1)",
        pointerEvents: "none",
      }} />
    )}
    {tabs.map(([v, l]) => (
      <button
        key={v}
        data-tab={String(v)}
        onClick={() => onChange(v)}
        style={{
          position: "relative", zIndex: 1,
          padding: "6px 14px", borderRadius: 6, border: "none", cursor: "pointer",
          fontFamily: "inherit", fontSize: 12,
          // Without this the buttons compress to fit and the labels truncate;
          // the point is for the BAR to scroll, not the labels to shrink.
          flexShrink: 0, whiteSpace: "nowrap",
          fontWeight: active === v ? 700 : 400,
          background: "transparent",
          color: active === v ? C.brandText : C.textSecondary,
          transition: "color 150ms ease, font-weight 0s",
        }}
      >{l}</button>
    ))}
  </div>
  )
  );
};

// ─── Insurance Select ─────────────────────────────────────────────────────────
export const InsuranceSelect = ({ value, onChange }) => (
  <select
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    style={{
      width: "100%", padding: "9px 12px",
      border: `1px solid ${C.borderMid}`, borderRadius: 8,
      fontSize: 13, fontFamily: "inherit", outline: "none",
      color: C.textPrimary, background: C.bgPrimary, boxSizing: "border-box",
      WebkitAppearance: "none",
      transition: "border-color 0.15s ease, box-shadow 0.15s ease",
    }}
    onFocus={(e) => {
      e.target.style.borderColor = "var(--brand-primary-mid)";
      e.target.style.boxShadow = "0 0 0 3px color-mix(in srgb, var(--brand-primary) 16%, transparent)";
    }}
    onBlur={(e) => {
      e.target.style.borderColor = C.borderMid;
      e.target.style.boxShadow = "none";
    }}
  >
    <option value="">Select insurance...</option>
    {NC_PAYER_GROUPS.map((g) => (
      <optgroup key={g.group} label={g.group}>
        {g.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </optgroup>
    ))}
  </select>
);

// ─── Stat Card ────────────────────────────────────────────────────────────────
export const StatCard = ({ label, value, sub, color = "var(--brand-primary)", icon }) => (
  <Card>
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
      <div>
        <div style={{ fontSize: 11, fontWeight: 600, color: C.textSecondary, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
        <div style={{ fontSize: 28, fontWeight: 800, color, letterSpacing: "-0.02em", marginTop: 6, fontVariantNumeric: "tabular-nums" }}>{value}</div>
        {sub && <div style={{ fontSize: 11, color: C.textTertiary, marginTop: 4 }}>{sub}</div>}
      </div>
      {icon && <div style={{ fontSize: 20, opacity: 0.55, color }}>{icon}</div>}
    </div>
  </Card>
);

// ─── Appointment Type Dot ─────────────────────────────────────────────────────
export const ApptTypeDot = ({ type, size = "sm" }) => {
  const t = DEFAULT_APPT_TYPES.find((x) => x.name === type) || DEFAULT_APPT_TYPES[1];
  const d = size === "sm" ? 7 : 10;
  // User-chosen colour: adapt so a mid-tone picked on white still reads on dark.
  return <span style={{ display: "inline-block", width: d, height: d, borderRadius: "50%", background: adaptColor(t.dot, { min: 3 }), flexShrink: 0 }} />;
};

// ─── Code Search Modal (ICD-10 / CPT picker) ──────────────────────────────────
export const CodeSearchModal = ({ title, codes, onAdd, onClose }) => {
  const [q, setQ] = useState("");
  const filtered = useMemo(
    () => codes.filter((c) => c.code.toLowerCase().includes(q.toLowerCase()) || c.description.toLowerCase().includes(q.toLowerCase())),
    [codes, q]
  );
  return (
    <Modal title={title} onClose={onClose} maxWidth={600}>
      <Input label="Search" value={q} onChange={setQ} placeholder="Search by code or description..." />
      <div style={{ maxHeight: 360, overflowY: "auto", border: `0.5px solid ${C.borderLight}`, borderRadius: 8 }}>
        {filtered.map((c) => (
          <div
            key={c.code}
            onClick={() => { onAdd(c); onClose(); }}
            style={{
              padding: "10px 14px", borderBottom: `0.5px solid ${C.borderLight}`,
              cursor: "pointer", display: "flex", gap: 12,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = C.bgSecondary)}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--brand-text)", minWidth: 64, fontFamily: "monospace" }}>{c.code}</div>
            <div style={{ fontSize: 12, color: C.textSecondary }}>{c.description}</div>
          </div>
        ))}
        {filtered.length === 0 && <div style={{ padding: 20, textAlign: "center", color: C.textTertiary, fontSize: 12 }}>No matches</div>}
      </div>
    </Modal>
  );
};

// ─── Empty State ──────────────────────────────────────────────────────────────
// EmptyState (UI Excellence item 10). An empty screen is a moment of doubt:
// "is it broken, or is there nothing here?" A brand-tinted mark answers that
// visually before the text is read, and `action` gives the one thing that fills
// it. The mark is drawn from the brand colour so it retints per practice.
//
// `icon` (Oct 2026): call sites pass a name ("check"), an emoji, or an icon
// element. Strings used to render as 36px grey TEXT, so Today showed the word
// "check" and Schedule the word "People". Every string now resolves to a line
// icon from components/icons.jsx inside the same tinted disc; an unknown
// string falls back to the default mark, never to text. Element icons sit in
// the disc too. The strokes draw in once (pos-draw; off under reduced motion).
const EMPTY_ICON = {
  check: IconCheck, users: IconUsers, clipboard: IconClipboard, calendar: IconCalendar, edit: IconEdit,
  chart: IconChart, pill: IconPill, flask: IconFlask, map: IconMapPin, shield: IconShield, search: IconSearch,
  bell: IconBell, message: IconMessage, inbox: IconInbox, clock: IconClock, receipt: IconReceipt,
  door: IconLogOut, layers: IconLayers, file: IconFileText, card: IconCreditCard,
  sparkles: IconSparkles, phone: IconPhone,
};
const EMPTY_DISC = {
  width: 56, height: 56, borderRadius: "50%", marginBottom: 2,
  display: "flex", alignItems: "center", justifyContent: "center",
  background: "rgba(var(--brand-primary-rgb, 15,110,86), 0.06)",
  boxShadow: "inset 0 0 0 1px rgba(var(--brand-primary-rgb, 15,110,86), 0.16)",
  color: C.brandText,
};
const DefaultEmptyMark = () => (
  <svg width="56" height="56" viewBox="0 0 56 56" aria-hidden="true" style={{ marginBottom: 2 }}>
    <circle cx="28" cy="28" r="26" fill="rgba(var(--brand-primary-rgb, 15,110,86), 0.06)" />
    <circle cx="28" cy="28" r="26" fill="none" stroke="rgba(var(--brand-primary-rgb, 15,110,86), 0.18)" strokeWidth="1" strokeDasharray="3 5" />
    <path d="M18 34c3-6 7-9 10-9s7 3 10 9" fill="none"
      stroke="rgba(var(--brand-primary-rgb, 15,110,86), 0.45)" strokeWidth="2" strokeLinecap="round" />
    <circle cx="28" cy="21" r="3.2" fill="rgba(var(--brand-primary-rgb, 15,110,86), 0.45)" />
  </svg>
);
export const EmptyState = ({ icon = null, title, sub, action }) => {
  const Named = EMPTY_ICON[emptyIconName(icon)] || null;
  const mark = Named
    ? <div className="pos-empty-icon" style={EMPTY_DISC} aria-hidden="true"><Named size={24} /></div>
    : (icon && typeof icon === "object")
      ? <div style={EMPTY_DISC} aria-hidden="true">{icon}</div>
      : <DefaultEmptyMark />;
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", padding: 48, textAlign: "center", gap: 8,
      animation: "pos-fadeUp 220ms cubic-bezier(0.2,0.8,0.2,1) both",
    }}>
      {mark}
      <div style={{ fontSize: 14, fontWeight: 700, color: C.textPrimary }}>{title}</div>
      {sub && <div style={{ fontSize: 12, color: C.textTertiary, maxWidth: 340, lineHeight: 1.55 }}>{sub}</div>}
      {action && <div style={{ marginTop: 10 }}>{action}</div>}
    </div>
  );
};

// ─── Loading / Error helpers ──────────────────────────────────────────────────
// Loader: the default "something is loading here" state for cards, tabs,
// panels and modals. Until Sep 2026 it rendered grey "Loading..." text, which
// flashed on every fast load and then snapped to content. It now renders a
// compact shimmer block (heading line + three text lines, about the height the
// old text took) so the swap to real content is quiet. The label is kept as the
// accessible name, so screen readers still hear "Loading your caseload...".
// Whole pages should use the shaped skeletons in components/skeletons.jsx.
// `small` renders an inline pill for use beside text ("Thinking...").
export const Loader = ({ label = "Loading...", small }) => {
  if (small) {
    return (
      <span role="status" aria-label={label} style={{ display: "inline-flex", verticalAlign: "middle" }}>
        <Skeleton w={36} h={10} r={999} />
      </span>
    );
  }
  return (
    <div role="status" aria-busy="true" aria-label={label}
      style={{ width: "100%", boxSizing: "border-box", padding: "16px 4px", display: "flex", flexDirection: "column", gap: 10 }}>
      <Skeleton w="38%" h={12} />
      <Skeleton w="92%" h={10} />
      <Skeleton w="80%" h={10} />
      <Skeleton w="64%" h={10} />
    </div>
  );
};

// Shimmering placeholder block for skeleton loading states. Compose several to
// mirror the shape of the content that's loading, so the layout doesn't jump.
export const Skeleton = ({ w = "100%", h = 16, r = 6, style }) => (
  <div style={{
    width: w, height: h, borderRadius: r, flexShrink: 0,
    background: "linear-gradient(90deg, " + C.hairline + " 25%, var(--pos-skeleton-band) 50%, var(--pos-skeleton-band-soft) 75%)",
    backgroundSize: "200% 100%",
    animation: "posSkeleton 1.3s ease-in-out infinite",
    ...style,
  }} />
);

// Reusable skeleton layouts so any view can drop in a shape-matching loader.
export const SkeletonStats = ({ count = 4 }) => (
  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
    {Array.from({ length: count }).map((_, i) => <Skeleton key={i} h={96} r={12} />)}
  </div>
);

export const SkeletonList = ({ rows = 5, height = 64 }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
    {Array.from({ length: rows }).map((_, i) => <Skeleton key={i} h={height} r={10} />)}
  </div>
);

// onRetry: a failed READ gets "Try again" next to the message. Pair it with
// hiding the list's empty state while the error shows, so a failure never
// reads as "nothing here".
export const ErrorBanner = ({ message, onDismiss, onRetry, retryLabel = "Try again" }) => (
  <div style={{
    background: C.redBg, color: C.red, border: `0.5px solid ${C.redBorder}`,
    padding: "10px 14px", borderRadius: 8, fontSize: 12, margin: "12px 0",
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap",
  }}>
    <span style={{ minWidth: 0, flex: "1 1 180px" }}>⚠ {message}</span>
    <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
      {onRetry && (
        <button type="button" onClick={onRetry} style={{
          background: C.bgPrimary, border: `0.5px solid ${C.redBorder}`, color: C.red, borderRadius: 6,
          cursor: "pointer", fontSize: 12, fontWeight: 600, padding: "6px 12px", minHeight: 32, fontFamily: "inherit",
        }}>{retryLabel}</button>
      )}
      {onDismiss && (
        <button type="button" aria-label="Dismiss" onClick={onDismiss} style={{
          background: "none", border: "none", color: C.red,
          cursor: "pointer", fontSize: 16, padding: 0, lineHeight: 1,
        }}>×</button>
      )}
    </span>
  </div>
);
